/**
 * 音量（BGM / SE ミュート状態）の実行時ストア。
 *
 * 設計方針（`luckyanimal-sweeper` のパターンに準拠）:
 *   - 設定の **永続化** は `services/settings.ts`（localStorage）。
 *   - 設定の **実行時反映** は本ファイルのモジュール変数 + 購読で行う。
 *     - 再生中の BGM（`useAudio`）は subscribe して即時 audio.volume を更新。
 *     - SE（`services/se.ts`）は `playSe()` 内で `isSeEffectivelyMuted()` を参照。
 *   - 永続化と実行時反映を二重に行うのは、HTMLAudioElement / Oscillator が
 *     設定読み込みを自発的に行わない（pull 型にできない）ため。
 *
 * 広告再生時のダッキング:
 *   - `setAdMuted(true)` で BGM/SE を一時的にミュート、終了時に `setAdMuted(false)`。
 *   - ユーザー設定のミュートとは独立に層が重なる（OR 判定）。
 *   - 本タイトルは現状 `services/adsStub.ts` が `window.confirm()` のみで同期
 *     ダイアログのため未使用。AdMob / 自社広告 SDK を統合する際に呼び出す。
 *
 * 初期値:
 *   - `initVolumeFromSettings()` を `main.tsx` の `initSettingsStorage()` 完了後に
 *     呼ぶことで、保存済みのユーザー設定を起動時に反映する。
 */

import { BGM_PLAYBACK_VOLUME } from '../config/settings'
import { loadSettings } from './settings'
import { isCapacitorNative } from '../utils/platform'

// ---------------------------------------------------------------------------
// 内部状態（モジュールレベル変数）
// ---------------------------------------------------------------------------

// `initVolumeFromSettings()` が走る前 / localStorage を読めなかった場合のフォールバック。
// ブラウザは音量調整の摩擦を避けるため無音で起動し、ネイティブアプリは
// 「ゲームアプリ」として起動される前提で従来どおり ON 既定にする
// （`services/settings.ts` の read 側ゲートと同じ分岐）。
const FALLBACK_MUTED = !isCapacitorNative()

let bgmMuted = FALLBACK_MUTED
let seMuted = FALLBACK_MUTED
let adMuted = false

type BgmVolumeListener = (effectiveVolume: number) => void
const bgmVolumeListeners = new Set<BgmVolumeListener>()

// ---------------------------------------------------------------------------
// BGM
// ---------------------------------------------------------------------------

/**
 * ミュート状態 × 広告ダッキングを畳み込んだ「実効 BGM 音量」。
 * ユーザーがミュート中 or 広告再生中は 0、それ以外は `BGM_PLAYBACK_VOLUME`。
 */
export function getEffectiveBgmVolume(): number {
  return bgmMuted || adMuted ? 0 : BGM_PLAYBACK_VOLUME
}

/** BGM ミュート状態を設定し、購読者に実効音量を通知する。 */
export function setBgmMuted(value: boolean): void {
  if (bgmMuted === value) return
  bgmMuted = value
  notifyBgmVolumeChanged()
}

/**
 * BGM の実効音量変更を購読する。登録直後には通知されない（購読側が自分で
 * 初期値を `getEffectiveBgmVolume()` から取る想定）。戻り値は unsubscribe。
 */
export function subscribeBgmVolume(listener: BgmVolumeListener): () => void {
  bgmVolumeListeners.add(listener)
  return () => {
    bgmVolumeListeners.delete(listener)
  }
}

function notifyBgmVolumeChanged(): void {
  const v = getEffectiveBgmVolume()
  for (const listener of bgmVolumeListeners) {
    try {
      listener(v)
    } catch (err) {
      // fallback:ok — 個別リスナーの例外が他の購読者への通知を止めないよう隔離。
      //   音量通知は UI 側の副作用（audio.volume セット等）で、失敗しても
      //   次回の通知で整合を取り戻せる程度の軽微な処理に限定される想定。
      console.warn('[volume] bgm listener threw:', err)
    }
  }
}

// ---------------------------------------------------------------------------
// SE
// ---------------------------------------------------------------------------

/** SE が鳴るべきかどうか（ユーザーミュート or 広告ダッキング中は false）。 */
export function isSeEffectivelyMuted(): boolean {
  return seMuted || adMuted
}

/** SE ミュート状態を設定する（通知は不要。`playSe()` が毎回 pull 参照するため）。 */
export function setSeMuted(value: boolean): void {
  seMuted = value
}

// ---------------------------------------------------------------------------
// 広告ダッキング
// ---------------------------------------------------------------------------

/**
 * 広告再生中のミュート層を設定する。BGM/SE 両方にかかる。
 * ユーザー設定のミュートとは独立に OR で合成される。
 */
export function setAdMuted(value: boolean): void {
  if (adMuted === value) return
  adMuted = value
  notifyBgmVolumeChanged()
}

// ---------------------------------------------------------------------------
// 初期化
// ---------------------------------------------------------------------------

/**
 * `loadSettings()` の結果で BGM/SE のミュート状態を初期化する。
 * `main.tsx` で `initSettingsStorage()` の後に一度だけ呼ぶ想定。
 *
 * 音量ストアのミュート状態は起動後は UI（タイトルの 🔊 / 🔇 トグルと SettingsModal）
 * からしか変化しないため、アプリ内で再読み込みする必要はない（どちらも save 後に
 * `setBgmMuted` / `setSeMuted` を呼ぶ）。
 */
export function initVolumeFromSettings(): void {
  const settings = loadSettings()
  bgmMuted = settings.bgmMuted
  seMuted = settings.seMuted
  // 起動時は購読者がまだ存在しないため通知しない（`useAudio` がマウント時に
  // `getEffectiveBgmVolume()` で初期値を取る）。
}
